import { TableState } from './types';
import { TableAction } from './actions/types';

export const createInitialState = <T>(
  initialFilters: Record<string, any> = {}
): TableState<T> => ({
  data: [],
  loading: false,
  loadingFilters: false,
  error: null,
  currentPage: 1,
  pageSize: 10,
  totalItems: 0,
  totalPages: 0,
  searchString: '',
  filters: { ...initialFilters },
  initialFilters: { ...initialFilters },
  sortField: null,
  sortDirection: null,
  selectedItems: new Set(),
  lastFetchParams: null,
  filtersReferential: []
});

export const tableReducer = <T>(
  state: TableState<T>,
  action: TableAction
): TableState<T> => {
  switch (action.type) {
    case 'SET_CURRENT_PAGE':
      return { ...state, currentPage: action.payload };

    case 'SET_PAGE_SIZE':
      return { ...state, pageSize: action.payload, currentPage: 1 };

    case 'SET_SEARCH_STRING':
      return { ...state, searchString: action.payload, currentPage: 1 };

    case 'CLEAR_SEARCH':
      return { ...state, searchString: '', currentPage: 1 };

    case 'SET_SORTING':
      return {
        ...state,
        sortField: action.payload.field,
        sortDirection: action.payload.direction,
        currentPage: 1
      };

    case 'CLEAR_SORTING':
      return {
        ...state,
        sortField: null,
        sortDirection: null,
        currentPage: 1
      };

    case 'SET_FILTER':
      return {
        ...state,
        filters: { ...state.filters, [action.payload.key]: action.payload.value },
        currentPage: 1
      };

    case 'REMOVE_FILTER': {
      const newFilters = { ...state.filters };
      delete newFilters[action.payload];
      return { ...state, filters: newFilters, currentPage: 1 };
    }

    case 'CLEAR_FILTERS':
      return { ...state, filters: { ...state.initialFilters }, currentPage: 1 };

    case 'SELECT_ITEM': {
      const newSelectedItems = new Set(state.selectedItems);
      newSelectedItems.add(action.payload);
      return { ...state, selectedItems: newSelectedItems };
    }

    case 'DESELECT_ITEM': {
      const newSelectedItems = new Set(state.selectedItems);
      newSelectedItems.delete(action.payload);
      return { ...state, selectedItems: newSelectedItems };
    }

    case 'SELECT_ALL_ITEMS': {
      const newSelectedItems = new Set(state.selectedItems);
      action.payload.forEach(id => newSelectedItems.add(id));
      return { ...state, selectedItems: newSelectedItems };
    }

    case 'CLEAR_SELECTION':
      return { ...state, selectedItems: new Set() };

    case 'RESET_TABLE':
      return createInitialState<T>(state.initialFilters);

    case 'SET_LOADING':
      return { ...state, loading: action.payload };

    case 'SET_LOADING_FILTERS':
      return { ...state, loadingFilters: action.payload };

    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };

    case 'SET_DATA':
      return {
        ...state,
        loading: false,
        data: action.payload.items,
        totalItems: action.payload.totalItems,
        totalPages: action.payload.totalPages,
        currentPage: action.payload.currentPage,
        error: null
      };

    case 'SET_FILTERS_REFERENTIAL':
      return {
        ...state,
        filtersReferential: action.payload,
        loadingFilters: false
      };

    case 'UPDATE_LAST_FETCH_PARAMS':
      return {
        ...state,
        lastFetchParams: JSON.stringify({
          page: state.currentPage,
          pageSize: state.pageSize,
          search: state.searchString,
          filters: state.filters,
          sort: state.sortField,
          direction: state.sortDirection
        })
      };

    default:
      return state;
  }
};
